export const CHECKOUT_URL = "https://endo-en.impultienda.ar/checkout/";

export const images = {
  hero: "https://cdn.magicpatterns.com/uploads/b3U9G6L1pjBYQ23uBeQpHC/ENDO-EN.png",
  creator: "https://cdn.magicpatterns.com/uploads/sW2hpHt7C6TNPqBZBjaWxn/DR._NATHAN_BROOKS_-_ORTHO.png",
};

export const masterMapSteps = [
  { title: "Case Information", text: "Gather what is available before anything else." },
  { title: "Clinical Findings", text: "Organize history, symptoms and examination." },
  { title: "Diagnostic Reasoning", text: "Connect findings and record your rationale." },
  { title: "Complexity Review", text: "Flag the factors that need more assessment." },
  { title: "Treatment Objectives", text: "Structure goals around the case review." },
  { title: "Documentation & Follow-Up", text: "Record decisions and open questions." },
];

export const problemList = [
  "Switching between different references to review related concepts.",
  "Trying to remember which information is still missing.",
  "Reviewing anatomy without having a clear picture of the case's overall complexity.",
  "Struggling to organize clinical findings into a coherent discussion.",
  "Spending too much time preparing educational case presentations.",
  "Looking for a consistent way to document your reasoning.",
];

export const beforeList = [
  "You review several disconnected resources.",
  "You struggle to see how different endodontic concepts relate to one another.",
  "You spend time organizing your notes before discussing a case.",
  "You have no consistent visual framework for your initial case review.",
  "You find it difficult to identify and document missing information systematically.",
  "You repeatedly recreate study materials and reference sheets.",
];

export const afterList = [
  "You have a master map connecting the major areas of endodontics.",
  "You can organize your initial case review using a repeatable sequence.",
  "You can document findings, questions, and information gaps more systematically.",
  "You have dedicated visual references for different endodontic topics.",
  "You can use structured worksheets to prepare case discussions.",
  "You have a reusable educational reference instead of repeatedly rebuilding your own notes.",
];

export const situations = [
  {
    title: "You Have the Knowledge, but You Want a Clearer Structure",
    text: "You understand the concepts of endodontics, but connecting them into an organized case review can still take time.",
  },
  {
    title: "You Spend Too Much Time Reviewing Notes",
    text: "Before discussing a case, you find yourself opening multiple documents and references to organize the information you need.",
  },
  {
    title: "You Want a Better Way to Structure Clinical Questions",
    text: "You need a consistent way to distinguish the information available from the questions that still need to be answered.",
  },
  {
    title: "You Want to Study More Efficiently",
    text: "You don't necessarily need another extensive textbook. You want a visual reference that makes related concepts easier to navigate.",
  },
  {
    title: "You Prepare Clinical Cases for Discussion",
    text: "You want a more organized way to present findings, document your reasoning, and explain the questions that require further evaluation.",
  },
  {
    title: "You Want a Practical Reference You Can Reuse",
    text: "You prefer having structured maps, worksheets, and checklists available instead of repeatedly recreating your own materials.",
  },
];

export const volumes = [
  {
    number: "I",
    title: "The Endo Master Map™",
    tagline: "See How the Major Areas of Endodontics Connect",
    intro:
      "I created this volume to give you the big picture. Instead of looking at diagnosis, anatomy, treatment concepts, and follow-up as completely separate subjects, you'll have a visual framework that shows how these areas relate.",
    inside: [
      "The Endodontic Master Map.",
      "A visual overview of the endodontic case-review process.",
      "A map of the major knowledge areas.",
      "A guide to navigating the six volumes.",
      "A glossary of essential concepts.",
      "A master reference index.",
      "A printable overview for quick consultation.",
    ],
    goal: "Give you one visual starting point whenever you want to organize your understanding of an endodontic case.",
    note: "",
  },
  {
    number: "II",
    title: "The Endo Diagnostic Framework™",
    tagline: "Organize Clinical Findings Before Drawing Conclusions",
    intro:
      "Diagnosis requires the integration of history, symptoms, examination findings, and appropriate diagnostic tests. This volume provides a structured educational framework for organizing that information.",
    inside: [
      "A clinical information worksheet.",
      "A symptom and finding organization map.",
      "A diagnostic testing record.",
      "A framework for organizing pulpal and apical diagnostic categories.",
      "A differential-diagnosis worksheet.",
      "An information-gap checklist.",
      "A guide to documenting the reasoning behind your conclusions.",
      "Educational case examples.",
    ],
    goal: "Help you organize the information available and identify what requires further evaluation before reaching a conclusion.",
    note: "The framework supports clinical reasoning but does not diagnose patients automatically.",
  },
  {
    number: "III",
    title: "The Endo Anatomy & Complexity Map™",
    tagline: "Build a More Organized Understanding of Root Canal Anatomy",
    intro:
      "Anatomical knowledge is central to endodontics, but anatomical concepts can become difficult to organize when you're reviewing a specific tooth or case. This volume brings those concepts together visually.",
    inside: [
      "Root and canal anatomy reference maps.",
      "Tooth-group anatomy summaries.",
      "Educational diagrams of canal configurations.",
      "Anatomical variation reference sheets.",
      "A framework for recording anatomical considerations.",
      "A case-complexity discussion worksheet.",
      "Anatomy review questions.",
      "A visual terminology guide.",
    ],
    goal: "Give you an organized educational reference for studying anatomy and documenting factors that may influence case complexity.",
    note: "",
  },
  {
    number: "IV",
    title: "The Endo Treatment Concepts Atlas™",
    tagline: "Connect Instrumentation, Irrigation, and Obturation in One Visual Reference",
    intro:
      "Rather than presenting isolated definitions, this volume organizes key treatment concepts into a visual learning framework.",
    inside: [
      "An instrumentation concepts map.",
      "An irrigation principles reference.",
      "A visual overview of shaping concepts.",
      "An obturation concepts map.",
      "A guide to the relationship between treatment objectives and documentation.",
      "Educational diagrams.",
      "Concept comparison tables.",
      "Questions for reviewing foundational principles.",
      "A scientific reference list.",
    ],
    goal: "Help you navigate related endodontic concepts without having to reconstruct the entire subject from disconnected notes.",
    note: "This is an educational reference, not a universal procedural protocol.",
  },
  {
    number: "V",
    title: "The Endo Clinical Pathways™",
    tagline: "Organize Your Review of Different Endodontic Scenarios",
    intro:
      "Different clinical situations require different information and different questions. I created this volume to help you organize those questions into visual pathways — for initial diagnostic assessment, anatomical complexity review, previously treated teeth, dental trauma, possible complications, and follow-up information.",
    inside: [
      "A visual overview.",
      "Key information categories.",
      "Questions to consider.",
      "Potential information gaps.",
      "Documentation prompts.",
      "Relevant educational references.",
    ],
    goal: "Give you a consistent structure for organizing your review of different endodontic scenarios.",
    note: "These pathways are educational frameworks. They do not prescribe patient-specific treatment or replace comprehensive evaluation.",
  },
  {
    number: "VI",
    title: "The Endo Case Review & Documentation System™",
    tagline: "Turn Your Case Information Into a Clear, Organized Record",
    intro: "The final volume brings the previous concepts into a practical documentation framework.",
    inside: [
      "A case overview worksheet.",
      "A clinical findings record.",
      "A diagnostic reasoning template.",
      "A treatment objectives worksheet.",
      "A procedure documentation template.",
      "A follow-up information record.",
      "A case presentation outline.",
      "A structured review checklist.",
      "A template for recording unresolved questions.",
    ],
    goal: "Help you document and present endodontic cases in a consistent, organized format.",
    note: "Designed to support professional documentation and education, not to replace your existing clinical records or applicable documentation requirements.",
  },
];

export const toolkit = [
  { title: "The Endodontic Master Map", text: "A one-page overview connecting the major subject areas covered by the system." },
  { title: "Clinical Decision Maps", text: "Visual references for organizing diagnostic questions, anatomical considerations, and case-review information." },
  { title: "Case Analysis Worksheets", text: "Structured worksheets for documenting clinical findings, questions, and conclusions." },
  { title: "Complexity Review Filters", text: "Educational prompts for recording factors that may require additional assessment." },
  { title: "Case Review Checklist", text: "A reusable checklist for organizing case information before a clinical discussion or presentation." },
  { title: "Printable and Digital Reference Sheets", text: "Use the materials in your preferred study environment, whether printed or on a tablet." },
];

export const bonuses = [
  {
    number: 1,
    title: "Endo Case Decoder™ — 20 Educational Practice Cases",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/3L62Ti1U1AY8wM88aTzFwX/BONUS_1_ENDO-EN.png",
    intro: "Practice organizing your reasoning through a collection of educational case scenarios. Each case includes:",
    items: [
      "A case summary.",
      "Relevant information and findings.",
      "Questions for analysis.",
      "Prompts for identifying missing information.",
      "Discussion questions.",
      "An educational answer guide.",
      "References for further study.",
    ],
    note: "The cases are designed for learning and discussion, not for automatically determining treatment for real patients.",
  },
  {
    number: 2,
    title: "The 15-Minute Endo Case Review Protocol™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/w2ZvEVwZkCpUyCpegAcpLL/BONUS_2_ENDO-EN.png",
    intro:
      "A condensed worksheet that follows a simple sequence: OBSERVE → ORGANIZE → IDENTIFY GAPS → REVIEW → DOCUMENT → REASSESS. Inside, you'll find:",
    items: [
      "A one-page case overview.",
      "A clinical information checklist.",
      "A diagnostic reasoning section.",
      "An information-gap record.",
      "A complexity review section.",
      "A documentation summary.",
    ],
    note: "The 15-minute target applies to completing the organizational worksheet when the necessary information is available. It does not guarantee that a complete clinical evaluation can be performed in that time.",
  },
  {
    number: 3,
    title: "The Endo Error & Knowledge Gap Library",
    value: 39,
    image: "https://cdn.magicpatterns.com/uploads/sPyWVuNUuHndkgz5NcaAyi/BONUS_3_ENDO-EN.png",
    intro: "Educational examples to help you recognize gaps in understanding and questions that deserve further review. Topics include:",
    items: [
      "Confusing symptoms with diagnostic conclusions.",
      "Overlooking missing information.",
      "Misinterpreting the limits of a radiographic image.",
      "Treating anatomical variation as irrelevant.",
      "Failing to document uncertainty.",
      "Drawing conclusions from incomplete information.",
      "Confusing educational principles with patient-specific decisions.",
      "Neglecting to revisit unresolved questions.",
    ],
    note: "The purpose is to improve learning and critical thinking — not to suggest that every clinical complication results from a single reasoning error.",
  },
  {
    number: 4,
    title: "30 ChatGPT Prompts for Endodontic Study",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/jW9PY7gKqQjo5srLtXwnCw/BONUS_4_ENDO-EN.png",
    intro: "A curated collection of prompts for using ChatGPT as an educational assistant. Use them to:",
    items: [
      "Generate endodontic review questions.",
      "Create fictional practice scenarios.",
      "Test your understanding of anatomical concepts.",
      "Prepare case-discussion questions.",
      "Organize notes from scientific articles.",
      "Create study flashcards.",
      "Build revision exercises.",
      "Identify concepts that need further verification.",
    ],
    note: "Intended for education and study, not for delegating patient-specific diagnosis or treatment decisions to AI.",
  },
  {
    number: 5,
    title: "The 30-Point Endo Study & Case Review Checklist™",
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/myEouhB7LG7fm3DkC1exAP/BONUS_5_ENDO-EN.png",
    intro: "A practical checklist for organizing the review of endodontic cases and educational material. Use it when:",
    items: [
      "Preparing a case discussion.",
      "Reviewing clinical findings.",
      "Studying diagnostic concepts.",
      "Organizing case documentation.",
      "Preparing an academic presentation.",
      "Reviewing follow-up information.",
      "Identifying unresolved questions.",
    ],
    note: "Designed to support systematic review without replacing professional judgment or the requirements of your clinical setting.",
  },
];

export const stackItems = [
  { label: "Endo Blueprint™ — 6 Premium Volumes", value: 97 },
  { label: "Bonus #1 — Endo Case Decoder: 20 Practice Cases", value: 47 },
  { label: "Bonus #2 — The 15-Minute Endo Case Review Protocol", value: 37 },
  { label: "Bonus #3 — The Endo Error & Knowledge Gap Library", value: 39 },
  { label: "Bonus #4 — 30 ChatGPT Prompts for Endodontic Study", value: 29 },
  { label: "Bonus #5 — The 30-Point Endo Study & Case Review Checklist", value: 19 },
];

export const guaranteeList = [
  "Explore all six volumes.",
  "Review the Endodontic Master Map.",
  "Open the clinical pathways.",
  "Work through the educational cases.",
  "Try the case-review worksheets.",
  "Review the checklists and study resources.",
];

export const lookFor = [
  { title: "Clear Organization", text: "Can you understand how the major subject areas relate to one another?" },
  { title: "Practical Educational Resources", text: "Do the maps and worksheets help you structure your study and case discussions?" },
  { title: "Reusability", text: "Can you return to the materials when reviewing a different topic or preparing an educational case?" },
];

export const faqs = [
  { q: "How will I receive the material?", a: "After completing your purchase, you'll receive instructions for accessing the Endo Blueprint™ and its included bonuses in digital format." },
  { q: "Is Endo Blueprint™ a course?", a: "No. It's a digital educational reference system composed of six volumes, visual maps, worksheets, checklists, and practice resources." },
  { q: "Is this product intended for endodontists only?", a: "It's designed primarily for endodontic education and professional reference. Its suitability depends on your training, experience, and intended use." },
  { q: "Does it replace clinical training or professional judgment?", a: "No. Endo Blueprint™ is an educational resource. It does not replace formal training, specialist consultation, current scientific evidence, or patient-specific clinical evaluation." },
  { q: "Will it help me diagnose a patient in 15 minutes?", a: "The 15-minute target refers to organizing an initial case review using the included worksheet when the required information is available. Diagnosis and treatment planning depend on the individual case and may require additional assessment." },
  { q: "Can I print the materials?", a: "The worksheets and reference pages are designed for convenient digital use and printing where applicable." },
  { q: "Is this a subscription?", a: "No. The offer is a one-time payment of $27 USD for the included digital materials. No recurring subscription is included." },
  { q: "Can I use the materials with real patient information?", a: "You may adapt suitable worksheets to your professional workflow, subject to your local requirements. Protect patient confidentiality and do not enter identifiable patient information into AI tools without the necessary authorization and safeguards." },
  { q: "What if I decide the product isn't right for me?", a: "Your purchase includes a 7-day guarantee, subject to the published refund terms. Follow the instructions provided at checkout to request a refund within the guarantee period." },
  { q: "Are the clinical references reviewed?", a: "The final published edition should include verified scientific references and clinical review appropriate to its contents. The product should not be used as a substitute for consulting current evidence or professional guidance." },
];

export const finalList = [
  "6 Premium Educational Volumes.",
  "The Endodontic Master Map.",
  "Clinical Decision Maps.",
  "Educational Clinical Pathways.",
  "Case Analysis Worksheets.",
  "20 Educational Practice Cases.",
  "The 15-Minute Endo Case Review Protocol.",
  "5 Professional Bonuses.",
  "Instant Digital Access.",
  "A 7-Day Guarantee.",
];

export const recentBuyers = [
  { name: "Valentina R.", country: "Colombia", flag: "CO" },
  { name: "Mateo G.", country: "Mexico", flag: "MX" },
  { name: "Lucía F.", country: "Argentina", flag: "AR" },
  { name: "Santiago P.", country: "Chile", flag: "CL" },
  { name: "Camila T.", country: "Peru", flag: "PE" },
  { name: "Andrés M.", country: "Spain", flag: "ES" },
  { name: "Daniela V.", country: "Ecuador", flag: "EC" },
  { name: "Javier L.", country: "Uruguay", flag: "UY" },
  { name: "Sofía C.", country: "Costa Rica", flag: "CR" },
  { name: "Diego A.", country: "Dominican Republic", flag: "DO" },
  { name: "Mariana S.", country: "Guatemala", flag: "GT" },
  { name: "Carlos E.", country: "Paraguay", flag: "PY" },
];
